import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TurnrModalContainer, TurnrModalContent, TurnrModalHeader } from "~/components/modals/TurnrModal";
import { getActualProductId, getProductName, getProductPropertiesAsString } from "~/cores/productCore";
import PropertySelector from "./propertySelector/PropertySelector";
import { Button } from "~/components/buttons/Buttons";
import SimilarProducts from "./SimilarProducts";
import { RETURN_METHOD_EXCHANGE } from "~/constants";
import { serviceIsLoading } from "rylak-common";
import { SkeletonLoaderWrapper } from "~/components/skeletons/SkeletonLoaderWrapper";
import { ServiceErrorAlert } from "~/components/alerts/Error";
import _ from "lodash";
import { getPropertyValue } from "~/cores/appCore";
import ResaleShippingSelector from "./ResaleShippingSelector";
import * as styles from "./ExchangeStepper.module.scss";
import { parsePropertyMap } from "./propertySelector/propertyCore";
import BigProductImage from "./BigProductImage";

export function createExchangeMethodState({ fromProduct, toProduct, variant, isTurnrExchange = null }) {
    return {
        productId: fromProduct?.sessionItemId,
        exchangeToProductId: toProduct ? toProduct?.actualProductId : fromProduct?.actualProductId,
        exchangeAlternativeId: variant?.id,
        isTurnrExchange,

        returnMethodType: RETURN_METHOD_EXCHANGE,
        swapProduct: toProduct,
    };
}

export function getSelectedExchangeAlternativeId({ exchangeAlternatives, exchangePropertyChoices, product }) {
    return exchangeAlternatives?.find((alternative) => {
        if (!alternative?.hasInventory) {
            return false;
        }
        return alternative?.properties?.every((p) => {
            const propertyTypeChoice = exchangePropertyChoices[p.type];
            if (_.isNil(propertyTypeChoice) && p.value === getPropertyValue({ product, propertyType: p.type })) {
                return true;
            }
            return propertyTypeChoice === p.value;
        });
    })?.id;
}

export default function ExchangeStepper(props) {
    const { appProps, backToMethodSelector, onModalClose, product, productExchangeServiceData, onSave } = props;
    const { getLocaleText, addAlert } = appProps;

    const [swapToProduct, setSwapToProductLocal] = useState(null);
    const [selectedProperties, setSelectedProperties] = useState({});
    const [validated, setValidated] = useState(false);
    const [isTurnrExchangeSelected, setIsTurnrExchangeSelected] = useState(false);

    const setSwapToProduct = useCallback((product) => {
        setSwapToProductLocal(product);
        setSelectedProperties({});
        setValidated(false);
    }, []);

    useEffect(() => {}, []);

    const serviceIsLoadingExchanges = serviceIsLoading(productExchangeServiceData);

    const exchangeProducts = productExchangeServiceData?.data?.products;
    const selectedProduct = swapToProduct ? swapToProduct : product;

    let exchangeAlternatives;
    let propertyMap;
    if (!swapToProduct) {
        exchangeAlternatives = exchangeProducts?.find((p) => p.id === getActualProductId(product))?.variants;
        propertyMap = parsePropertyMap(exchangeAlternatives, selectedProduct);
    } else {
        exchangeAlternatives = swapToProduct?.variants;
        propertyMap = parsePropertyMap(swapToProduct?.variants, swapToProduct);
    }

    let selectedAlternativeIdLocal = getSelectedExchangeAlternativeId({
        exchangeAlternatives,
        exchangePropertyChoices: selectedProperties,
        product: selectedProduct,
    });

    const selectedAlternative = exchangeAlternatives?.find((e) => e.id === selectedAlternativeIdLocal);

    const visuallyDisabled = !selectedAlternative;

    function getSelectSomePropertyError() {
        return !selectedAlternative && getLocaleText("return-items-exchange-invalid-at-least-one");
    }

    function getMorePropertiesNeededError() {
        return Object.keys(selectedProperties).length > 0 && !selectedAlternative && getLocaleText("return-items-exchange-invalid-input");
    }

    function getErrorText() {
        return getMorePropertiesNeededError() || getSelectSomePropertyError();
    }

    const onSaveClick = useCallback(() => {
        if (visuallyDisabled) {
            setValidated(true);
            addAlert({
                content: getErrorText(),
            });
        } else {
            const exchangeMethodData = createExchangeMethodState({
                fromProduct: product,
                toProduct: swapToProduct,
                variant: selectedAlternative,
                isTurnrExchange: isTurnrExchangeSelected,
            });
            onSave(exchangeMethodData);
        }
    }, [selectedAlternative, product, swapToProduct, addAlert, isTurnrExchangeSelected]);

    const showPropertyError = validated && getSelectSomePropertyError();

    const componentProps = useMemo(() => {
        return {
            ...props,
            swapToProduct,
            setSwapToProduct,
            validated,
            exchangeAlternatives,
            selectedProduct,
            selectedProperties,
            setSelectedProperties,
            showPropertyError,
            propertyMap,
            selectedAlternative,
            isTurnrExchangeSelected,
            setIsTurnrExchangeSelected,
        };
    }, [props, validated, selectedProperties, setSelectedProperties, swapToProduct, setSwapToProduct, propertyMap, exchangeAlternatives]);

    const exchangeCustomMessage = getLocaleText("exchange-custom-message");

    return (
        <TurnrModalContainer>
            <TurnrModalHeader onBack={() => backToMethodSelector()} onClose={onModalClose} title={getLocaleText("exchange-step-title")} />
            <TurnrModalContent>
                <div className={styles["container"]}>
                    <BigProductImage {...componentProps} />
                    <div className={styles["content-container"]}>
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>{getProductName(selectedProduct)}</div>
                                {/*<div className={styles["purchase-price"]}>*/}
                                {/*    {formatCurrency({*/}
                                {/*        amount: selectedProduct?.purchasePrice,*/}
                                {/*        currency: currencyCode,*/}
                                {/*    })}*/}
                                {/*</div>*/}
                            </div>
                            <div style={{ visibility: swapToProduct && "hidden" }}>
                                <span className={styles["purchased"]}>{getLocaleText("returning")}:</span>
                                &nbsp;
                                <span
                                    style={{
                                        fontWeight: 400,
                                        fontSize: "13px",
                                    }}
                                >
                                    {getProductPropertiesAsString(product)}
                                </span>
                            </div>
                        </div>
                        <ServiceErrorAlert serviceData={productExchangeServiceData} getLocaleText={getLocaleText} style={{ marginTop: "10px", marginBottom: "10px" }} localeKey={"product-exchange"} />
                        <SkeletonLoaderWrapper loading={serviceIsLoadingExchanges} loadingMinHeight={"150px"} border={true}>
                            <PropertySelector {...componentProps} />
                            {exchangeCustomMessage && (
                                <div
                                    style={{
                                        marginTop: "16px",
                                        marginBottom: "16px",
                                    }}
                                >
                                    {exchangeCustomMessage}
                                </div>
                            )}
                            <ResaleShippingSelector {...componentProps} />
                            <SimilarProducts {...componentProps} />
                        </SkeletonLoaderWrapper>
                        <Button
                            appProps={appProps}
                            className={styles["select-button"]}
                            dataTestId={"exchange-button-select"}
                            visuallyDisabled={visuallyDisabled}
                            onClick={onSaveClick}
                            fullWidth={true}
                            text={getLocaleText("exchange-select-button")}
                        />
                    </div>
                </div>
            </TurnrModalContent>
        </TurnrModalContainer>
    );
}
